<template>
  <div class="map-container">
    <div class="map-alerts">
      <CAlert v-for="(errorText, index) in map.errors" :key="`error-${index}`" size="sm" color="warning">
        {{ errorText }}
      </CAlert>
    </div>

    <div class="map-mode">
      <CSelect class="p-0 m-0" size="sm" :value.sync="map.mode.value" :options="map.mode.choices" />
    </div>


    <l-map @ready="handleMapReady" @update:bounds="handleMapMove">
      <l-tile-layer :url="map.mode.value" :attribution="map.attribution" />

      <!-- port polygons -->
      <!--
      // replaced by Leaflet.Draw

      <template v-if="port.portId">
        <l-geo-json v-for="(zone, index) in portEntitiesZones"
                    :geojson="zone.geoJSON"
                    :key="`zone-${index}`"
                    :options="getGeoJSONLayerOptions(zone, 'port')"
                    @mouseover="evt => handlePolygonMouseOver(evt, zone, 'port')"
                    @mouseout="evt => handlePolygonMouseOut(evt, zone, 'port')"
                    @click="evt => handleZoneMouseClick(evt, zone, 'port')">
        </l-geo-json>
      </template>
      -->

      <!-- port point -->
      <l-marker v-if="portCoordinates"
                :lat-lng="portCoordinates"
                :options="{zone: portZone, permanentTooltip: true}"
                :icon="portIcon">
        <!-- tooltip -->
        <l-tooltip :options="{permanent: true}">
          <span>{{ getNested(port, 'portName', '--') }} ({{ port.portId }})</span>
        </l-tooltip>

        <!-- popup -->
        <l-popup :options="{permanent: true, interactive: true}">
          <div class="port-popup">
            <div class="title">
              Port
            </div>
            <div class="details-row">
              <div class="details-row__title">Name</div>
              <div class="details-row__value">{{ port.portName }}</div>
            </div>
            <div class="details-row">
              <div class="details-row__title">Coordinates</div>
              <div class="details-row__value">{{ portCoordinates.join(',') }}</div>
            </div>
          </div>
        </l-popup>
      </l-marker>

      <!-- in area polygons -->
      <l-geo-json v-for="(zone, index) in this.inAreaPolygons"
                  :geojson="zone.geoJSON"
                  :key="`inarea-zone-${index}`"
                  :options="getGeoJSONLayerOptions(zone, 'inarea')"
                  @mouseover="evt => handlePolygonMouseOver(evt, zone, 'inarea')"
                  @mouseout="evt => handlePolygonMouseOut(evt, zone, 'inarea')"
                  @click="evt => handleZoneMouseClick(evt, zone, 'inarea')">
      </l-geo-json>

      <!-- in area points -->
      <l-marker v-for="(point, index) in this.inAreaPoints"
                :key="`inarea-point-${index}`"
                :options="{zone: point}"
                :permanent="true"
                :lat-lng="point.coordinates"
                :icon="getInAreaIcon(point)"
                @click="evt => handleZoneMouseClick(evt, point, 'inarea')">
        <l-tooltip :options="{permanent: true}">
          <span>{{ getNested(point, 'name', '--') }} ({{ point.id }})</span>
        </l-tooltip>
      </l-marker>
    </l-map>

    <!-- modals -->
    <map-entities-save-state-modal
      :mode="confirmationModal.mode"
      :changed-layers="confirmationModal.changedLayers"
      @save="handleConfirmModalSave"
      @cancel="handleConfirmModalCancel"
      :show.sync="confirmationModal.show" />

    <map-entity-create-modal
      :show.sync="createModal.show"
      :title="`Add Berth to port &quot;${this.port.portName}&quot; (${this.port.portId})`"
      :zone="createModal.zone"
      :layer="createModal.layer"
      @save="handleCreateModalConfirm"
      @cancel="handleCreateModalCancel" />
  </div>
</template>

<script>
import debounce from 'lodash-es/debounce'
import startCase from 'lodash-es/startCase'
import getNested from 'lodash-es/get'
import flatten from 'lodash-es/flatten'
// import cloneDeep from 'lodash-es/cloneDeep'

import L from 'leaflet'
import {
  LMap,
  LTileLayer,
  LMarker,
  LPopup,
  LTooltip,
  LGeoJson,
} from 'vue2-leaflet'

import 'leaflet-draw'
import 'leaflet-draw/dist/leaflet.draw.css'

import { mapActions } from 'vuex'

import {
  portMapStyles,
  inareaMapStyles,
} from '../../constants'

import {
  patchDrawToolbarSaveMethod,
  patchEditToolbarSaveMethod,
} from '../../utils/leaflet'

import MapEntitiesSaveStateModal from './modals/MapEntitiesSaveStateModal'
import MapEntityCreateModal from './modals/MapEntityCreateModal'

const maxInAreaEntitiesCount = 500;


export default {
  name: 'port-details-map',

  components: {
    LMap,
    LTileLayer,
    LMarker,
    LPopup,
    LTooltip,
    LGeoJson,
    MapEntitiesSaveStateModal,
    MapEntityCreateModal,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },

    port: {
      type: Object,
      default: () => ({}),
    },

    berths: {
      type: Array,
      default: () => ([]),
    },

    shipyards: {
      type: Array,
      default: () => ([]),
    },

    anchorages: {
      type: Array,
      default: () => ([]),
    },
  },

  data() {
    return {
      rawInAreaZones: [],

      drawControl: null,
      confirmationModal: {
        show: false,
        mode: 'edit',  // "edit" or "remove"
        changedLayers: [],
        saveMethod: null,
        cancelMethod: null,
      },
      createModal: {
        show: false,
        title: '--',
        zone: {},
        layer: null,
      },

      map: {
        mapObject: null,
        errors: [],
        editMode: false,

        mode: {
          value: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',
          choices: [
            // { text: 'Dark Map', value: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png' },
            { label: 'Satellite', value: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}' },
            { label: 'Light Map', value: 'http://a.tile.openstreetmap.org/{z}/{x}/{y}.png' },
          ]
        },
        attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',

        styles: {
          port: portMapStyles,
          inarea: inareaMapStyles,
        },
      },
    };
  },

  created() {
    patchDrawToolbarSaveMethod(this.handleZonesCreate);
    patchEditToolbarSaveMethod(this.handleZonesSave, false);
    patchDrawToolbar();
  },

  destroyed() {
    this.destroyMapDraw();
  },

  methods: {
    getNested: getNested,

    ...mapActions(
      'polygons', [
        'listInAreaEntities',
        'getAnchorage',
      ],
    ),

    handleMapReady(mapObject) {
      this.map.mapObject = mapObject;
      this.setMapCenter();
      if(!this.loading) {
        this.setMapBounds();
        this.initMapDraw();
      }
    },

    setMapCenter() {
      if(this.map.mapObject && this.port.geometryCoordinates) {
        let portPoint = JSON.parse(this.port.geometryCoordinates);
        let coordinates = [portPoint.coordinates[1], portPoint.coordinates[0]]
        this.map.mapObject.setView(coordinates, 11);
      }
    },

    setMapBounds() {
      // more precise map centering and zooming after all enteties have been loaded
      let coordinates = [];

      coordinates = coordinates.concat(
        this.getCoordinatesList(this.port.geometryCoordinates)
      );
      coordinates = coordinates.concat(
        this.getCoordinatesList(this.port.geometryPolygon)
      );

      ['berths', 'anchorages', 'shipyards'].forEach(sectionName => {
        this[sectionName].forEach(item => {
          coordinates = coordinates.concat(
            this.getCoordinatesList(item.geometryPolygon)
          );
        });
      });

      let longs = coordinates.map(item => item[0]);
      let lats = coordinates.map(item => item[1]);
      this.map.mapObject.flyToBounds(
        [
          [ Math.min(...lats), Math.min(...longs) ],
          [ Math.max(...lats), Math.max(...longs) ],
        ],
        { maxZoom: 13 }
      );
    },

    getCoordinatesList(geoJSON) {
      if(!geoJSON) {
        return [];
      }

      if(typeof geoJSON === 'string') {
        geoJSON = JSON.parse(geoJSON);
      }

      if(geoJSON.type.toLowerCase() === 'polygon') {
        return flatten(geoJSON.coordinates);
      } else if(geoJSON.type.toLowerCase() === 'point') {
        return [geoJSON.coordinates];
      }
    },

    // leaflet draw section (create/update/delete entities) -->
    initMapDraw() {
      let map = this.map.mapObject;
      let drawnItems = new L.FeatureGroup(this.portEntitiesPolygons);
      map.addLayer(drawnItems);

      this.drawControl = new L.Control.Draw({
        position: 'topright',
        draw: {
          marker: false,
          circle: false,
          circlemarker: false,
          polyline: false,
          rectangle: false,
        },
        edit: {
          edit: true,
          remove: {
            removeAll: false,
          },
          featureGroup: drawnItems,
        },
      });
      map.addControl(this.drawControl);

      // EVENTS
      map.on('draw:editstart', this.setEditModeOn);  // polygons edite mode started
      map.on('draw:editcancel', this.setEditModeOff);  // polygons edited mode stoped
      map.on('draw:deletestart', this.setEditModeOn);  // polygons remove mode is started
      map.on('draw:deletestop', this.setEditModeOff);  // polygons remove mode is stopped
      map.on('draw:created', this.handlePolygonCreated);  // polygon created
      map.on('draw:edited', this.setEditModeOff);  // polygons edited and saved
    },

    destroyMapDraw() {
      if(!this.drawControl) {
        return;
      }

      let map = this.map.mapObject;
      map.off('draw:editstart');
      map.off('draw:editcancel');
      map.off('draw:deletestart');
      map.off('draw:deletestop');
      map.off('draw:created');
      map.off('draw:edited');
      map.removeLayer(this.drawControl.options.edit.featureGroup);
      map.removeControl(this.drawControl);

      this.drawControl = null;
    },

    setEditModeOn() {
      this.map.editMode = true;
    },

    setEditModeOff() {
      this.map.editMode = false;
    },

    handlePolygonCreated(evt) {
      this.handleZonesCreate(evt);
    },

    handleZonesSave(evt, saveMethod, cancelMethod) {
      // save / remove layers are handled here
      // changedLayers could be "edited layers" or "removed layers"
      this.confirmationModal.show = true;
      this.confirmationModal.mode = evt.type;  // "edit" or "remove"
      this.confirmationModal.changedLayers = evt.layers;
      this.confirmationModal.saveMethod = saveMethod;
      this.confirmationModal.cancelMethod = cancelMethod;
    },

    handleConfirmModalSave() {
      this.confirmationModal.saveMethod();
      this.$emit('refresh');
    },

    handleConfirmModalCancel() {
      this.confirmationModal.cancelMethod();
    },

    handleZonesCreate(evt) {
      if(this.createModal.show) {
        // event is duplicated when we click on the menu item "finish"
        // so it will create another zone if we don't prevent this
        return;
      }

      this.drawControl.options.edit.featureGroup.addLayer(evt.layer);
      this.map.mapObject.addLayer(evt.layer);
      this.createModal.layer = evt.layer;

      const entityType = evt.layerType.split('_')[0];
      this.createModal.zone = {
        portId: this.port.portId,
        type: entityType,
        geoJSON: evt.layer.toGeoJSON(),
      };
      this.createModal.show = true;
    },

    handleCreateModalConfirm() {
      this.$emit('refresh');
    },

    handleCreateModalCancel(layer) {
      this.map.mapObject.removeLayer(layer);
    },
    // leaflet draw section (create/update/delete entities) <--

    handleMapMove: debounce(async function(data) {
      if(this.loading) {
        return;
      }

      const upperLeft = [data['_southWest']['lng'], data['_northEast']['lat']]
      const lowerRight = [data['_northEast']['lng'], data['_southWest']['lat']]
      const lowerLeft = [upperLeft[0], lowerRight[1]]
      const upperRight = [lowerRight[0], upperLeft[1]]

      this.rawInAreaZones = await this.listInAreaEntities({lowerLeft, upperRight});
      if(this.rawInAreaZones.error) {
        console.error('can\'t receive inarea enteties: api error');
        this.rawInAreaZones = [];
      }
    }, 500),

    handlePolygonMouseOver({target: layer}, zone, displayType) {
      let zoneStyle = this.getZoneStyle(zone, displayType);
      
      if (displayType === 'inarea' || zone.type !== 'port')  {
        layer.setStyle({...zoneStyle, fillOpacity: 0.6});
      }

      if(displayType === 'port' && !this.map.editMode) {
        // note, we don't show port entities tooltips when edit mode is active
        layer.bindTooltip(
          zone.name,
          { className: 'map-tooltip' },
        ).openTooltip();
      }
    },

    handlePolygonMouseOut({target: layer}, zone, displayType) {
      this.closeZoneTooltips(zone);
      this.resetEntityStyle(layer, zone, displayType);
    },

    async handleZoneMouseClick({target: layer}, zone, displayType) {
      this.closeZoneTooltips(zone);

      // ports aren't editable for now for example
      const editableZoneTypes = ['berth', 'anchorage', 'shipyard'];

      if(displayType === 'port' && editableZoneTypes.includes(zone.type) && !this.map.editMode) {
        // note, we don't show port entities tooltips when edit mode is active
        this.resetEntityStyle(layer, zone, displayType);
        this.$emit('edit-entity', zone, zone.type);
        return;
      }

      if(displayType === 'inarea') {
        await this.openNearbyPolygonPopup(layer, zone);
      }
    },

    async openNearbyPolygonPopup(layer, zone) {
      const zoneTitle = startCase(zone.type);
      let portLink = '';

      if(zone.portId) {
        portLink = `<a href="#/ports/${zone.portId}" class="h5">Open port</a>`;
      } else if(zone.type === 'port') {
        portLink = `<a href="#/ports/${zone.id}" class="h5">Open port</a>`;
      } else if(zone.type === 'anchorage') {
        let anchorage = await this.getAnchorage(zone.id);
        portLink = `<a href="#/ports/${anchorage.primaryPort}" class="h5">Open port</a>`;
      }

      const popupContent = `
        <div class="port-popup">
          <div class="title">
            ${zoneTitle}
          </div>
          <div class="details-row">
            <div class="details-row__title">${zoneTitle} Id</div>
            <div class="details-row__value">${zone.id}</div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Port Id</div>
            <div class="details-row__value">${zone.portId}</div>
          </div>
          <div class="details-row">
            <div class="details-row__title">Name</div>
            <div class="details-row__value">${getNested(zone, 'name', '--')}</div>
          </div>
          <div class="text-center mt-2 mb-3">
            ${portLink}
          </div>
        </div>
      `;
      layer.bindPopup(popupContent).openPopup();
    },

    closeZoneTooltips(zone) {
      // "layer.closeTooltip()" doesn't work when map is zooming,
      // it's needed to remove tooltips manually
      const map = this.map.mapObject;

      map.eachLayer(tooltipLayer => {
        if(tooltipLayer.options.pane !== 'tooltipPane') {
          return;
        }
        if(tooltipLayer.options.permanent) {
          // don't close permanent tooltips
          // it's possible to have a few tooltip with the same id and entity type
          return;
        }

        const layerZone = getNested(tooltipLayer, '_source.options.zone', {});
        const parentLayer = getNested(tooltipLayer, '_source', null);
        if(layerZone.id === zone.id && layerZone.type === zone.type) {
          parentLayer.unbindTooltip();
          map.removeLayer(tooltipLayer);
        }
      });
    },

    resetEntityStyle(layer, zone, displayType) {
      let zoneStyle = this.getZoneStyle(zone, displayType);
      layer.setStyle(zoneStyle);
    },

    getGeoJSONLayerOptions(zone, displayType) {
      return {
        style: this.getZoneStyle(zone, displayType),
        zone: zone,
      };
    },

    getEntityMapDetails(entity, entityType) {
      return {
        id: entity[`${entityType}Id`],
        name: entity[`${entityType}Name`],
        type: entityType,
        portId: entity.portId,
        primaryPort: entity.primaryPort || null,
        geoJSON: JSON.parse(entity.geometryPolygon),
      };
    },

    getZoneStyle(zone, displayType) {
      return this.map.styles[displayType][`${zone.type}`];
    },

    getInAreaIcon(point) {
      if(point.type === 'port') {
        return L.divIcon({
          html: '<i class="fa fa-anchor"></i>',
          // iconSize: [10, 15],
          iconAnchor: [10, 30],
          className: 'inarea-icon'
        });
      } else {
        return L.divIcon({
          html: '<i class="fa fa-map-marker"></i>',
          // iconSize: [20, 20],
          iconAnchor: [8, 30],
          className: 'inarea-icon'
        });
      }
    },
  },

  computed: {
    inAreaPolygons() {
      return this.inAreaEntities.filter(
        item => item.geometryPolygon
      ).map(item => ({
        id: item.id,
        portId: item.portId,
        type: item.type,
        name: item.name,
        geoJSON: JSON.parse(item.geometryPolygon),
      }));
    },

    inAreaPoints() {
      return this.inAreaEntities.filter(
        item => item.geometryCoordinates
      ).map(item => {
        const geoJSON = JSON.parse(item.geometryCoordinates)

        return {
          id: item.id,
          type: item.type,
          name: item.name,
          coordinates: [
            geoJSON.coordinates[1],
            geoJSON.coordinates[0]
          ],
        };
      });
    },

    inAreaEntitiesCount() {
      return this.inAreaEntities.length;
    },

    inAreaEntities() {
      let entityHashes = this.portEntitiesZones.map(item => `${item.type}-${item.id}`);
      entityHashes.push(`port-${this.portId}`);

      // filters out current port entities to avoid duplicates
      let entities = this.rawInAreaZones.filter(item => {
        const itemHash = `${item.type}-${item.id}`;
        return !entityHashes.includes(itemHash);
      });

      return entities.slice(0, maxInAreaEntitiesCount);
    },

    portEntitiesPolygons() {
      return this.portEntitiesZones.map(zone => {
        let coordinates = L.GeoJSON.coordsToLatLngs(zone.geoJSON.coordinates, 1);
        let options = this.getGeoJSONLayerOptions(zone, 'port');
        const layer = new L.Polygon(coordinates, {...options.style, zone: options.zone});

        layer.on('mouseover', evt => this.handlePolygonMouseOver(evt, zone, 'port'));
        layer.on('mouseout', evt => this.handlePolygonMouseOut(evt, zone, 'port'));
        layer.on('click', evt => this.handleZoneMouseClick(evt, zone, 'port'));

        return layer;
      });
    },

    portEntitiesZones() {
      let zones = [];
      let portEntitiesMap = {
        berth: 'berths',  // green color polygon
        shipyard: 'shipyards',  // yellow color polygon
        anchorage: 'anchorages',  // red color polygon
      };

      if(this.port.geometryPolygon) {
        zones.push(this.getEntityMapDetails(this.port, 'port'));
      }

      Object.keys(portEntitiesMap).forEach(entityType => {
        let source = portEntitiesMap[entityType];

        this[source].forEach(item => {
          if(item.geometryPolygon) {
            zones.push(
              this.getEntityMapDetails(item, entityType)
            );
          }
        });
      });

      return zones;
    },

    portIcon() {
      return L.divIcon({
        html: '<i class="fa fa-anchor"></i>',
        // iconSize: [20, 20],
        iconAnchor: [23, 35],
        className: 'port-icon'
      });
    },

    portCoordinates() {
      if(this.port.geometryCoordinates) {
        let coordinates = JSON.parse(this.port.geometryCoordinates).coordinates;
        return [coordinates[1], coordinates[0]];
      } else {
        return null;
      }
    },

    portZone() {
      return {
        type: 'port',
        id: this.port.portId,
      };
    },

  },

  watch: {
    'port.portId'() {
      this.setMapCenter();
      this.destroyMapDraw();
    },

    loading(value) {
      if(!value) {
        this.setMapBounds();
        this.initMapDraw();
      }
    },

  },
}

function patchDrawToolbar() {
  const tooltips = {
    berth_polygon: {
      start: 'Click to start drawing berth.',
      cont: 'Click to continue drawing berth.',
      end: 'Click first point to close this berth.'
    }
  };

  L.Draw.BerthPolygon = L.Draw.Polygon.extend({
    options: {
        repeatMode: true
    },
    initialize: function (map, options) {
        this.entityType = 'berth';
        this.type = 'berth_polygon';
        this.featureTypeCode = 'berth_polygon';

        // class: leaflet-draw-draw-berth_polygon
        options.shapeOptions = portMapStyles.berth;
        L.Draw.Feature.prototype.initialize.call(this, map, options);
    },

    _getTooltipText: function () {
      let text, subtext;

      if (this._markers.length === 0) {
        text = tooltips[this.type].start;
      } else if (this._markers.length < 3) {
        text = tooltips[this.type].cont;
        subtext = this._getMeasurementString();
      } else {
        text = tooltips[this.type].end;
        subtext = this._getMeasurementString();
      }

      return {
        text: text,
        subtext: subtext
      };
    },

  });


  L.DrawToolbar.include({
    getModeHandlers: function (map) {
      // console.log('this.options', this.options);
      // let berthOptions = cloneDeep(this.options);
      // berthOptions.shapeOptions = portMapStyles.berth;

      return [
        {
          enabled: this.options.polygon,
          handler: new L.Draw.BerthPolygon(map, this.options),
          // title: L.drawLocal.draw.toolbar.buttons.polygon,
          title: 'Berth',
        },
      ];
    },
  });
}
</script>


<style scoped>
  .map-mode >>> .form-control {
    border-color: transparent;
    box-shadow: none;
    -webkit-box-shadow: none;
    color: #2b2b2b;
  }

  .map-container >>> a.leaflet-popup-close-button {
   top: 5px;
   right: 5px;
  }

  .map-container >>> .port-icon {
   font-size: 40px;
   color: #0054ff;
  }

  .map-container >>> .inarea-icon {
   font-size: 25px;
   color: rgba(0, 84, 255, 0.5);
  }

  .map-container >>> .alert {
   padding: 0.3rem 1.5rem 0.3rem 0.3rem;
   margin-bottom: 0.5rem;
  }

  .map-container >>> .alert-dismissible .close {
   padding: 0.3rem 0.5rem 0.3rem 0;
  }

  .map-container >>> .details-row {
    padding-bottom: 5px;
    margin-bottom: 5px;
    display: flex;
    justify-content: space-between;
    border-bottom: 1px dotted #d0d0d0;
  }

  .map-container >>> .details-row__title {
    min-width: 100px;
    padding-right: 10px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .map-container >>> .details-row__value {
    padding-left: 10px;
    font-weight: 500;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .map-container >>> .port-popup {
    font-size: 12px;
  }

  .map-container >>> .port-popup .title {
    margin-bottom: 10px;
    font-size: 16px;
  }

 .map-container >>> .leaflet-draw-draw-berth_polygon {
   background-image: none;
 }

 .map-container >>> .leaflet-draw-draw-berth_polygon:before {
   content: 'B';
   font-size: 16px;
   font-weight: bold;
   color: green;
 }
</style>

<style scoped lang="scss">
  .map-container {
    width: 100%;
    height: 750px;
    position: relative;

    .map-mode {
      z-index: 410;
      position: absolute;
      bottom: 25px;
      right: 5px;
      min-width: 120px;
      max-width: 150px;
      outline: none;
      opacity: 0.6;
      transition: opacity 0.3s;
    }

    .map-mode:hover {
      opacity: 0.9;
      transition: opacity 0.3s;
    }

    .map-alerts {
      z-index: 410;
      position: absolute;
      bottom: 0;
      left: 10px;
      min-width: 120px;
      max-width: calc(100% - 150px);
      max-height: 300px;
      overflow: hidden;
      opacity: 0.6;
      transition: opacity 0.3s;
    }

    .map-alerts:hover {
      opacity: 0.9;
      transition: opacity 0.3s;
    }
  }
</style>